import React from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ExternalLink, Calendar, Calculator, AlertCircle } from 'lucide-react';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

interface SimulationHistoryItem {
    url_pdf: string;
    data_criacao: string;
    valor_mensal?: string;
    valor_economia?: string;
    numero_simulacao?: string;
}

interface SimulationHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    simulations: SimulationHistoryItem[] | null | undefined;
}

const SimulationHistoryModal: React.FC<SimulationHistoryModalProps> = ({
    isOpen,
    onClose,
    simulations
}) => {
    if (!isOpen) return null;

    // Sort by date descending (newest first)
    const sortedSimulations = simulations && Array.isArray(simulations)
        ? [...simulations].sort((a, b) => new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime())
        : [];

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500 rounded-lg border border-green-100 dark:border-green-800/50">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins leading-tight">Histórico de Simulações</h2>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-0.5">Visualize todas as simulações geradas</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors shrink-0 -mr-2 sm:mr-0"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {sortedSimulations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-100 dark:border-slate-700/50 rounded-xl bg-gray-50/30 dark:bg-slate-800/30">
                            <div className="w-12 h-12 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-gray-400 dark:text-slate-500 mb-3">
                                <AlertCircle size={24} />
                            </div>
                            <h3 className="text-gray-900 dark:text-slate-100 font-medium">Nenhuma simulação encontrada</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-xs mt-1">Este cliente ainda não possui propostas geradas.</p>
                        </div>
                    ) : (
                        <div className="border border-gray-200 dark:border-slate-700 rounded-xl shadow-sm overflow-hidden bg-white dark:bg-slate-900">
                            {/* Mobile View: Cards */}
                            <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-800">
                                {sortedSimulations.map((sim, index) => (
                                    <div key={`mobile-${index}`} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors flex flex-col gap-3">

                                        <div className="flex justify-between items-start gap-4">
                                            <div className="min-w-0">
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Número</span>
                                                <div className="flex items-center gap-2">
                                                    <FileText size={14} className="text-gray-400 dark:text-slate-500 shrink-0" />
                                                    <span className="text-sm font-mono text-gray-700 dark:text-slate-200 font-medium truncate">
                                                        {sim.numero_simulacao || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="text-right shrink-0">
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Economia Anual</span>
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <Calculator size={14} className="text-green-500 dark:text-green-400" />
                                                    <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-md border border-green-100 dark:border-green-800/50">
                                                        {sim.valor_economia || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-end mt-1 pt-3 border-t border-gray-50 dark:border-slate-800/50">
                                            <div>
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1 block">Data de Criação</span>
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                                    <Calendar size={14} className="text-gray-400 dark:text-slate-500" />
                                                    {new Date(sim.data_criacao).toLocaleString('pt-BR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                            </div>

                                            <a
                                                href={sim.url_pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 hover:text-primary transition-all shadow-sm"
                                            >
                                                <span className="text-xs font-bold">Ver PDF</span>
                                                <ExternalLink size={12} />
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop View: Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                                    <thead className="bg-gray-50 dark:bg-slate-800/50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Número da proposta
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Data de criação
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Economia anual
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                                Ação
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                                        {sortedSimulations.map((sim, index) => (
                                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <FileText size={16} className="text-gray-400 dark:text-slate-500" />
                                                        <span className="text-sm font-mono text-gray-600 dark:text-slate-300 font-medium">
                                                            {sim.numero_simulacao || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                                                        <Calendar size={14} className="text-gray-400 dark:text-slate-500" />
                                                        {new Date(sim.data_criacao).toLocaleString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5">
                                                        <Calculator size={14} className="text-green-500 dark:text-green-400" />
                                                        <span className="text-sm font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-md border border-green-100 dark:border-green-800/50">
                                                            {sim.valor_economia || '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <a
                                                        href={sim.url_pdf}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-600 hover:text-primary transition-all shadow-sm group-hover:border-primary/30"
                                                    >
                                                        <span className="text-xs font-bold">Abrir PDF</span>
                                                        <ExternalLink size={12} />
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SimulationHistoryModal;
